import { Injectable } from '@angular/core';
import { PosthogService } from './posthog.service';
import {
    UserEvents,
    ExpenseEvents,
    ReportEvents,
    CompanyEvents,
    AccountantEvents,
    ErrorEvents,
    DuplicateEvents,
} from '../../common/events';
import { Expense } from 'on-shared/_models/Expense';
import { of } from 'rxjs';

// Type-safe interfaces for event properties
export interface UserSignupProperties {
    from_invitation: boolean;
    country_id?: number;
    currency_id?: number;
    language_code?: string;
}

export interface UserLoginProperties {
    login_method: 'password' | 'microsoft';
}

export interface UserJoinedCompanyProperties {
    company_id: number;
}

export interface ExpenseCreatedProperties {
    expense_id: number;
    expense_type?: string;
}

export interface ExpenseEditedProperties {
    expense_id: number;
}

export interface ExpenseDeletedProperties {
    expense_ids: number[];
}

export interface ExpenseAccuracyValidationProperties {
    expense_id: number;
    changed_fields: string[];
    changed_field_count: number;
    field_accuracy: 'perfect' | 'modified';
    accuracy_score: number;
    expense_type?: string;
}

export interface ExpenseImageSentProperties {
    expense_id?: number;
    format: string;
}

export interface UploadErrorProperties {
    error_type: string;
    format?: string;
    expense_id?: number;
}

export interface ReportCreatedProperties {
    report_id: number;
}

export interface ReportSentToValidationProperties {
    report_id: number;
}

export interface ReportApprovedProperties {
    report_id: number;
    total_amount: number;
    approved_amount: number;
    full_reimbursement: boolean;
}

export interface CompanyCreatedProperties {
    company_id: number;
}

export interface ExportArchivedProperties {
    export_id: number;
    probative_value_activated: boolean;
    [key: string]: string | number | boolean | undefined;
}

export interface ReportRefusedProperties {
    report_id: number;
    refusal_reason?: string;
    [key: string]: string | number | boolean | undefined;
}

export interface ExpenseRefusedProperties {
    expense_id: number;
    refusal_reason?: string;
    [key: string]: string | number | boolean | undefined;
}

export interface DuplicateSuspicionReviewedProperties {
    suspicion_id: number;
    confidence: number;
    expense_id: number;
    suspected_duplicate_expense_id: number;
    report_id?: number;
    company_id?: number;
}

@Injectable({
    providedIn: 'root',
})
export class AnalyticsService {
    constructor(private posthogService: PosthogService) {}

    // User Events
    trackUserSignup(properties: UserSignupProperties): void {
        this.posthogService.capture(UserEvents.SIGNUP, { ...properties });
    }

    trackUserLogin(properties: UserLoginProperties): void {
        this.posthogService.capture(UserEvents.LOGIN, { ...properties });
    }

    trackUserLogout(): void {
        this.posthogService.capture(UserEvents.LOGOUT);
    }

    trackUserJoinedCompany(properties: UserJoinedCompanyProperties): void {
        this.posthogService.capture(UserEvents.JOINED_COMPANY, {
            ...properties,
        });
    }

    // Expense Events
    trackExpenseCreated(properties: ExpenseCreatedProperties): void {
        this.posthogService.capture(ExpenseEvents.CREATED, { ...properties });
    }

    trackExpenseEdited(properties: ExpenseEditedProperties): void {
        this.posthogService.capture(ExpenseEvents.EDITED, { ...properties });
    }

    trackExpenseDeleted(properties: ExpenseDeletedProperties): void {
        this.posthogService.capture(ExpenseEvents.DELETED, { ...properties });
    }

    trackExpenseAccuracyValidation(
        properties: ExpenseAccuracyValidationProperties,
    ): void {
        this.posthogService.capture(ExpenseEvents.ACCURACY_VALIDATION, {
            ...properties,
        });
    }

    trackExpenseImageSent(properties: ExpenseImageSentProperties): void {
        this.posthogService.capture(ExpenseEvents.IMAGE_SENT, {
            ...properties,
        });
    }

    // Error Events
    trackUploadError(properties: UploadErrorProperties): void {
        this.posthogService.capture(ErrorEvents.UPLOAD_ERROR, {
            ...properties,
        });
    }

    // Report Events
    trackReportCreated(properties: ReportCreatedProperties): void {
        this.posthogService.capture(ReportEvents.CREATED, {
            ...properties,
        });
    }

    trackReportSentToValidation(
        properties: ReportSentToValidationProperties,
    ): void {
        this.posthogService.capture(ReportEvents.SENT_TO_VALIDATION, {
            ...properties,
        });
    }

    trackReportApproved(properties: ReportApprovedProperties): void {
        this.posthogService.capture(ReportEvents.APPROVED, {
            ...properties,
        });
    }

    // Company Events
    trackCompanyCreated(properties: CompanyCreatedProperties): void {
        this.posthogService.capture(CompanyEvents.CREATED, {
            ...properties,
        });
    }

    // Accountant Events
    trackExportArchived(properties: ExportArchivedProperties): void {
        this.posthogService.capture(AccountantEvents.EXPORT_ARCHIVED, {
            ...properties,
        });
    }

    // Refusal Events
    trackReportRefused(properties: ReportRefusedProperties): void {
        this.posthogService.capture(ReportEvents.REFUSED, {
            ...properties,
        });
    }

    trackExpenseRefused(properties: ExpenseRefusedProperties): void {
        this.posthogService.capture(ExpenseEvents.REFUSED, {
            ...properties,
        });
    }

    // Duplicate Suspicion Events
    trackDuplicateConfirmed(
        properties: DuplicateSuspicionReviewedProperties,
    ): void {
        this.posthogService.capture(DuplicateEvents.CONFIRMED, {
            ...properties,
        });
    }

    trackDuplicateDismissed(
        properties: DuplicateSuspicionReviewedProperties,
    ): void {
        this.posthogService.capture(DuplicateEvents.DISMISSED, {
            ...properties,
        });
    }

    // Helper method to calculate expense accuracy based on AI vs user comparison
    calculateExpenseAccuracy(
        originalExpense: Expense,
        modifiedExpense: Expense,
        expenseId: number,
    ): void {
        const changedFields: string[] = this.getChangedFields(
            originalExpense,
            modifiedExpense,
        );
        const changedFieldCount = changedFields.length;

        // AI recognizes 7 fields: amount, taxes, date, emitter, expense type, country, currency
        const totalAiFields = 7;
        const fieldAccuracy = changedFieldCount === 0 ? 'perfect' : 'modified';
        const accuracyScore =
            (totalAiFields - changedFieldCount) / totalAiFields;

        this.trackExpenseAccuracyValidation({
            expense_id: expenseId,
            changed_fields: changedFields,
            changed_field_count: changedFieldCount,
            field_accuracy: fieldAccuracy as 'perfect' | 'modified',
            accuracy_score: accuracyScore,
            expense_type: modifiedExpense.ExpenseType?.Code,
        });
    }

    /**
     * Compare original and current expense to identify changed fields
     */
    private getChangedFields(
        originalExpense: Expense,
        modifiedExpense: Expense,
    ): string[] {
        if (!originalExpense) return [];

        const changedFields: string[] = [];

        // 1. Global amount (with taxes)
        if (
            originalExpense.GlobalAmount?.TtcOrigin !==
            modifiedExpense.GlobalAmount?.TtcOrigin
        ) {
            changedFields.push('amount');
        }

        // 2. Taxes splitting
        for (const am of originalExpense.TVAAmounts) {
            const modifiedAmount = modifiedExpense.TVAAmounts.find(
                (item) => item.Id === am.Id,
            );
            if (
                !modifiedAmount ||
                modifiedAmount.TtcOrigin !== am.TtcOrigin ||
                modifiedAmount.HtOrigin !== am.HtOrigin ||
                modifiedAmount.TaxesOrigin !== am.TaxesOrigin
            ) {
                changedFields.push('taxes');
                break;
            }
        }

        // 3. Date
        if (
            new Date(originalExpense.DateOnExpense).toISOString() !==
            new Date(modifiedExpense.DateOnExpense).toISOString()
        ) {
            changedFields.push('date');
        }

        // 4. Emitter/Description
        if (originalExpense.Emitter !== modifiedExpense.Emitter) {
            changedFields.push('emitter');
        }

        // 5. Expense type
        if (
            originalExpense.ExpenseType?.Id !== modifiedExpense.ExpenseType?.Id
        ) {
            changedFields.push('expense_type');
        }

        // 6. Country
        if (originalExpense.Country?.Id !== modifiedExpense.Country?.Id) {
            changedFields.push('country');
        }

        // 7. Currency
        if (originalExpense.Currency?.Id !== modifiedExpense.Currency?.Id) {
            changedFields.push('currency');
        }

        return changedFields;
    }
}
